import React, { useEffect, useMemo, useState } from "react";
import { Input, Tag, Tree, type TreeDataNode } from "antd";
import styled from "styled-components";
import { Icon, IconContainer } from "../../components/visuals/styledComponents";
import {
  RowContainer,
  ElementContainer,
  HorizontalLine,
} from "../../components/containers/styledComponents";
import { MetricTreeNode } from "./MetricTreeNode";
import ChevronIcon from "../../assets/svg/down_arrow_icon.svg";
import metricsIcon from "../../assets/svg/metrics_icon_hovered.svg";
import { MESSAGES } from "../../messages";
import { onSelectionFuncType } from "../../components/visuals/PageFilter/PageFilter";
import { Filter } from "../../types/PageFilterTypes";
import { DeleteItem } from "../../components/tables/DeleteItem";
import { SelectableMetric } from "../../types/ScreenerTypes";
import {
  CompassModal,
  ModalContent,
  ModalFooter,
} from "../../components/common/Modal";

type TreeNodeInternal = {
  title?: string;
  key?: string;
  isLeaf?: boolean;
  data?: {
    key: string;
    displayValue: string;
    type: string;
    description?: string;
  };
  _children?: Record<string, TreeNodeInternal>;
};

const StyledTreeWrapper = styled(ElementContainer)`
  overflow-y: auto;
  .ant-tree-switcher {
    background: transparent !important;
    display: flex;
    align-items: center;
  }

  .ant-tree-switcher:hover {
    background: transparent !important;
  }

  .ant-tree-node-content-wrapper: hover;
`;

const Chevron = styled(Icon)<{ expanded?: boolean }>`
  transition: transform 0.2s;
  transform: ${({ expanded }): string =>
    expanded ? "rotate(0deg)" : "rotate(-90deg)"};
  width: 18px;
  height: 18px;
  pointer-events: none;
`;

const SelectedMetricsRowContainer = styled(RowContainer)`
  overflow-x: auto;
  height: 20px;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* Internet Explorer 10+ */

  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Edge */
  }
`;

export const MetricsModal: React.FC<ModalComponentProps> = ({
  show,
  compassMetrics,
  handleClose,
  handleSelectMetrics,
}) => {
  const [checkedKeys, setCheckedKeys] = useState<string[]>([]);
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");

  useEffect(() => {
    if (!searchTerm) return;

    const decorated = decorateTree(buildMetricTree(compassMetrics));
    const { expandedKeys: newExpandedKeys } = getAllMatchingKeys(
      decorated,
      searchTerm
    );

    setExpandedKeys(Array.from(newExpandedKeys));
  }, [searchTerm]);

  const clearAll = (): void => {
    setCheckedKeys([]);
    setSearchTerm("");
  };

  const toggleCheck = (key: string): void => {
    const node = findNode(treeData, key);
    if (!node) return;

    const keysToToggle = collectAllChildKeys(node);

    setCheckedKeys((prev) =>
      prev.includes(key)
        ? prev.filter((k) => !keysToToggle.includes(k))
        : [...prev, ...keysToToggle.filter((k) => !prev.includes(k))]
    );
  };

  const decorateTree = (nodes: TreeDataNode[], level = 0): TreeDataNode[] =>
    nodes.map((node) => {
      const isChecked = checkedKeys.includes(node.key as string);
      const isExpanded = expandedKeys.includes(node.key);

      return {
        ...node,
        title: (
          <MetricTreeNode
            title={node.title?.toString() || ""}
            isChecked={isChecked}
            onClick={(): void => toggleCheck(node.key as string)}
            level={level}
            isLeaf={node.isLeaf}
            key={node.key as string}
            data={(node as TreeNodeInternal).data}
          />
        ),
        switcherIcon: node.children ? (
          <Chevron expanded={isExpanded} src={ChevronIcon} />
        ) : null,
        children: node.children
          ? decorateTree(node.children, level + 1)
          : undefined,
      };
    });

  const treeData = useMemo(() => {
    const decorated = decorateTree(buildMetricTree(compassMetrics));
    if (!searchTerm) return decorated;

    const { matchedKeys } = getAllMatchingKeys(decorated, searchTerm);

    const filterTree = (nodes: TreeDataNode[]): TreeDataNode[] =>
      nodes
        .map((node) => {
          const key = node.key as string;
          const hasMatchingChild =
            node.children && filterTree(node.children).length > 0;
          const isMatch = matchedKeys.has(key);

          if (isMatch || hasMatchingChild) {
            return {
              ...node,
              children: node.children ? filterTree(node.children) : undefined,
            };
          }
          return null;
        })
        .filter(Boolean) as TreeDataNode[];

    return filterTree(decorated);
  }, [checkedKeys, expandedKeys, searchTerm, compassMetrics]);

  const selectedFilters: Filter[] = useMemo(() => {
    return checkedKeys
      .filter((key) => {
        const node = findNode(treeData, key);
        return node?.isLeaf;
      })
      .map((key) => {
        const node = findNode(treeData, key) as TreeNodeInternal;

        return {
          key,
          displayValue: node?.data?.displayValue ?? key,
          selectorName: "metric_id",
        };
      });
  }, [checkedKeys, treeData]);

  const handleApply = (): void => {
    handleClose();
    handleSelectMetrics(selectedFilters, "metric_id");
  };

  const footerContent = (
    <ModalFooter
      onClose={handleClose}
      closeText="Cancel"
      submitText={`Apply (${selectedFilters.length})`}
      onSubmit={handleApply}
      showDivider={false}
    />
  );

  return (
    <CompassModal
      show={show}
      handleClose={handleClose}
      size="lg"
      title="Select Metrics"
      titleIcon={metricsIcon}
      modalType="lg"
      footer={footerContent}
    >
      <ModalContent>
        <RowContainer mb={8} ai="center">
          <Input
            size="small"
            placeholder="Search metrics"
            value={searchTerm}
            onChange={(e): void => setSearchTerm(e.target.value)}
            prefix={
              <IconContainer>
                <Icon src={metricsIcon} />
              </IconContainer>
            }
          />
          <DeleteItem id={"clear-all"} isDeletable={true} onDelete={clearAll} />
        </RowContainer>
        <SelectedMetricsRowContainer jc="flex-start" mb={8}>
          {checkedKeys.map((key) => {
            const node = findNode(treeData, key) as TreeNodeInternal;
            if (!node?.isLeaf) return null;
            const title =
              node?.data?.displayValue || MESSAGES.NOT_AVAILABLE_TEXT;
            return (
              <Tag
                key={key}
                bordered={false}
                closable
                onClose={(): void => toggleCheck(key)}
              >
                {title}
              </Tag>
            );
          })}
        </SelectedMetricsRowContainer>
        <HorizontalLine marginTop={1} />
        <RowContainer mt={16}>
          <StyledTreeWrapper h="450px">
            <Tree
              checkable={false}
              selectable={false}
              blockNode
              expandedKeys={expandedKeys}
              onExpand={(keys): void => setExpandedKeys(keys)}
              treeData={treeData}
            />
          </StyledTreeWrapper>
        </RowContainer>
      </ModalContent>
    </CompassModal>
  );
};

interface ModalComponentProps {
  show: boolean;
  compassMetrics: SelectableMetric[];
  handleClose: () => void;
  handleSelectMetrics: onSelectionFuncType;
}

const collectAllChildKeys = (node: TreeDataNode): string[] => {
  const childKeys =
    node.children?.flatMap((child) => collectAllChildKeys(child)) || [];
  return [node.key as string, ...childKeys];
};

const findNode = (
  nodes: TreeDataNode[],
  key: string
): TreeDataNode | undefined => {
  for (const node of nodes) {
    if (node.key === key) return node;
    if (node.children) {
      const found = findNode(node.children, key);
      if (found) return found;
    }
  }
  return undefined;
};

export const buildMetricTree = (
  metrics: SelectableMetric[]
): TreeDataNode[] => {
  const root: Record<string, TreeNodeInternal> = {};

  metrics.forEach((metric) => {
    const levels = [metric.level_1, metric.level_2, metric.level_3];

    let currentNode = root;
    levels.forEach((levelName) => {
      if (!currentNode[levelName]) {
        currentNode[levelName] = { _children: {} };
      } else if (!currentNode[levelName]._children) {
        currentNode[levelName]._children = {};
      }
      currentNode = currentNode[levelName]._children || {};
    });

    currentNode[metric.key] = {
      title: `${metric.displayValue} (${metric.type})`,
      key: metric.key,
      isLeaf: true,
      data: {
        key: metric.key,
        displayValue: metric.displayValue,
        type: metric.type,
        description: metric.description || MESSAGES.NOT_AVAILABLE_TEXT,
      },
    };
  });

  const toTreeDataNodes = (
    nodes: Record<string, TreeNodeInternal>
  ): TreeDataNode[] =>
    Object.entries(nodes).map(([key, node]) => ({
      title: node.title || key,
      key: node.key || key,
      isLeaf: node.isLeaf,
      data: node.data,
      children: node._children ? toTreeDataNodes(node._children) : undefined,
    }));

  return toTreeDataNodes(root);
};

const getAllMatchingKeys = (
  nodes: TreeDataNode[],
  search: string
): { matchedKeys: Set<string>; expandedKeys: Set<string> } => {
  const matchedKeys = new Set<string>();
  const expandedKeys = new Set<string>();

  const dfs = (node: TreeDataNode, ancestors: string[] = []): void => {
    const label =
      (node.title as React.ReactElement)?.props?.title?.toLowerCase?.() || "";

    const match = label.includes(search.toLowerCase());
    if (match && node.key) {
      matchedKeys.add(node.key as string);
      ancestors.forEach((key) => expandedKeys.add(key));
    }

    node.children?.forEach((child) =>
      dfs(child, [...ancestors, node.key as string])
    );
  };

  nodes.forEach((node) => dfs(node));
  return { matchedKeys, expandedKeys };
};
